import React from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ArrowRight, Truck, ShieldCheck, Clock, Leaf } from "lucide-react";
import { DRYCLEAN_RATES, WASH_SERVICES, BRAND, IMAGES } from "../mock";
import { useBooking } from "../App";
import Reveal from "../components/Reveal";

const SERVICE_META = {
  "dry-cleaning": { title: "Dry Cleaning Service", cat: "DryClean", img: IMAGES.dryClean1, desc: "Professional dry cleaning that keeps your delicate garments fresh, crisp and long-lasting." },
  "organic-dry-cleaning": { title: "Organic Dry Cleaning", cat: "Organic Dryclean", img: IMAGES.dryClean3, desc: "Eco-friendly organic dry cleaning using biodegradable solvents — gentle on fabric and the planet." },
  "shoe-cleaning": { title: "Shoe Cleaning Service", cat: "Shoe Cleaning", img: IMAGES.dryClean2, desc: "Expert shoe cleaning and restoration for sneakers, leather, suede and more." },
  "bag-cleaning": { title: "Bag Cleaning Service", cat: "Bag Cleaning", img: IMAGES.handDelivery, desc: "Restore your backpacks, purses and trolleys to like-new condition." },
  "sofa-cleaning": { title: "Sofa Cleaning Service", cat: "Sofa Cleaning", img: IMAGES.facility, desc: "Deep cleaning for sofas, chairs and cushions right at your doorstep." },
  "jacket-cleaning": { title: "Leather Jacket Cleaning", cat: "Leather Jacket", img: IMAGES.machineCloseup, desc: "Specialised care for leather, suede and rexine jackets." },
  "laundry": { title: "Laundry / Wash Service", cat: null, img: IMAGES.foldedStack, desc: "Wash, iron and premium laundry services priced per kilo — fresh clothes, every time." },
};

const perks = [
  { icon: Truck, label: "Free Pickup & Delivery" },
  { icon: Clock, label: "48-Hour Turnaround" },
  { icon: ShieldCheck, label: "7-Day Guarantee" },
  { icon: Leaf, label: "Eco-Friendly Care" },
];

const ServicePage = () => {
  const { slug } = useParams();
  const { openBooking } = useBooking();
  const meta = SERVICE_META[slug] || SERVICE_META["dry-cleaning"];
  const cat = DRYCLEAN_RATES.find((c) => c.title === meta.cat);
  const isLaundry = slug === "laundry";

  return (
    <main>
      {/* Breadcrumb hero */}
      <section className="bg-gradient-to-br from-[#eef4fb] to-[#fdf3e6] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <p className="text-sm text-navy/50 mb-3">
              <Link to="/" className="hover:text-orange-brand">Home</Link> / <span className="text-navy/80">{meta.title}</span>
            </p>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-navy font-head leading-tight">{meta.title}</h1>
            <p className="text-navy/70 mt-4 leading-relaxed">{meta.desc}</p>
            <button onClick={openBooking} className="mt-6 inline-flex items-center gap-2 bg-orange-brand hover:bg-[#e0850f] text-white font-semibold px-7 py-3.5 rounded-full shadow-lg shadow-orange-brand/25 transition-all hover:gap-3">
              Book a Pickup <ArrowRight size={18} />
            </button>
          </Reveal>
          <Reveal delay={100}>
            <img src={`${meta.img}?w=700&q=80`} alt={meta.title} className="rounded-3xl shadow-xl w-full h-[320px] object-cover" />
          </Reveal>
        </div>
      </section>

      {/* Perks */}
      <section className="bg-white py-8 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {perks.map((p) => (
            <div key={p.label} className="flex items-center gap-3 justify-center bg-[#f7f9fc] rounded-xl py-4">
              <p.icon size={22} className="text-orange-brand" />
              <span className="font-semibold text-navy text-sm">{p.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Rate table */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-navy font-head text-center mb-8">{meta.title} Rates</h2>

          {!isLaundry && cat && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {cat.items.map((it, i) => (
                <div key={it.name} className={`flex items-center justify-between px-6 py-4 text-sm ${i % 2 ? "bg-[#f7f9fc]" : "bg-white"}`}>
                  <span className="text-navy/80 font-medium">{it.name}</span>
                  <span className="font-semibold text-navy">
                    {it.mrp && <span className="text-gray-400 line-through mr-2 font-normal">₹{it.mrp}</span>}
                    ₹{it.price}<span className="text-[11px] text-gray-400 font-normal">{cat.unit}</span>
                  </span>
                </div>
              ))}
            </div>
          )}

          {isLaundry && (
            <div className="grid sm:grid-cols-2 gap-5">
              {WASH_SERVICES.map((s) => (
                <div key={s.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <div className="flex items-baseline justify-between">
                    <h3 className="font-bold text-navy font-head">{s.title}</h3>
                    <span className="text-2xl font-extrabold text-orange-brand font-head">₹{s.price}<span className="text-sm text-navy/50">{s.unit}</span></span>
                  </div>
                  <p className="text-sm text-navy/60 mt-2">{s.desc}</p>
                  <ul className="mt-4 space-y-1.5">
                    {s.features.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-navy/75"><Check size={15} className="text-green-500" /> {f}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-gray-400 text-center mt-5">*Prices shown with coupon {BRAND.coupon} applied. Valid on first order. Inclusive of taxes.</p>
          <div className="text-center mt-8">
            <button onClick={openBooking} className="inline-flex items-center gap-2 bg-navy hover:bg-[#153a63] text-white font-semibold px-8 py-3.5 rounded-full transition-colors">
              Schedule Free Pickup <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicePage;
